import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { RefreshCw } from 'lucide-react';
import api from '../../../services/api';

function DepartmentsPage() {
  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingDivision, setEditingDivision] = useState(null);
  const [formData, setFormData] = useState({ name: '', department_id: '' });

  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    try {
  const response = await api.get('/api/departments/categories');
      
      if (response.data.status === 1 && response.data.data) {
        // Handle nested data structure: response.data.data.data
  const deptData = response.data.data.data || response.data.data;
        setDepartments(Array.isArray(deptData) ? deptData : []);
      } else {
        setDepartments([]);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Failed to load departments');
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDivisions = useCallback(async () => {
    try {
  const response = await api.get('/api/departments');
      if (response.data.status === 1 && response.data.data) {
  // divisions loaded
        setDivisions(response.data.data);
      } else {
        setDivisions([]);
      }
    } catch (error) {
      console.error('Error fetching divisions:', error);
      setDivisions([]);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
    fetchDivisions();
  }, [fetchDepartments, fetchDivisions]);

  const getDepartmentName = (divisionDepartment) => {
    if (!Array.isArray(departments)) return 'Unknown';
    const dept = departments.find(d => d.category_id === parseInt(divisionDepartment));
    return dept ? dept.category_name : 'Unknown';
  };

  const filteredDivisions = divisions.filter(div =>
    div.department_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getDepartmentName(div.division_department).toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4 py-4">
          <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
            <div className="flex justify-center items-center space-x-2">
              <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Departments & Divisions</h1>
                <p className="text-xs text-gray-500 mb-1">View organizational structure</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {departments.length} Departments • {divisions.length} Divisions
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  fetchDepartments();
                  fetchDivisions();
                }}
                disabled={loading}
                className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} style={{ color: loading ? '#0096DE' : 'currentColor' }} />
              </button>
              
              <input
                type="text"
                placeholder="Search divisions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              
              <button
                onClick={() => {
                  setEditingDivision(null);
                  setFormData({ name: '', department_id: '' });
                  setShowDialog(true);
                }}
                className="inline-flex items-center px-3 py-1.5 text-white text-sm font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200"
                style={{ backgroundColor: '#0087D4' }}
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Add Division
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Division Name</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Department</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredDivisions.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="px-3 py-6 text-center">
                      <div className="inline-flex flex-col items-center">
                        <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <p className="text-gray-600 text-lg font-semibold mb-1">No divisions found</p>
                        <p className="text-gray-400 text-sm">
                          {searchTerm ? "No divisions match your search." : "No divisions available."}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredDivisions.map(div => (
                    <tr key={div.department_id} className="hover:bg-blue-50">
                      <td className="px-3 py-2 text-sm font-medium text-gray-800">{div.department_name}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{getDepartmentName(div.division_department)}</td>
                      <td className="px-3 py-2 text-sm">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => {
                              setEditingDivision(div);
                              setFormData({ name: div.department_name, department_id: div.division_department });
                              setShowDialog(true);
                            }}
                            className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(div.department_id, div.department_name)}
                            className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create/Edit Dialog */}
        {showDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
              <h2 className="text-lg font-bold mb-4">{editingDivision ? 'Edit Division' : 'Add Division'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Division Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
                    <select
                      value={formData.department_id}
                      onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select a department</option>
                      {Array.isArray(departments) && departments.map(dept => (
                        <option key={dept.category_id} value={dept.category_id}>{dept.category_name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 justify-end mt-6">
                  <button
                    type="button"
                    onClick={() => setShowDialog(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-white rounded-md text-sm"
                    style={{ backgroundColor: '#0087D4' }}
                  >
                    {editingDivision ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      if (editingDivision) {
        const response = await api.put(`/api/department/${editingDivision.department_id}`, {
          department_id: editingDivision.department_id,
          department_name: formData.name,
          division_department: formData.department_id
        });
        if (response.data.status === 1) {
          toast.success('Division updated successfully');
          setShowDialog(false);
          fetchDivisions();
        }
      } else {
        const response = await api.post('/api/departments', {
          department_name: formData.name,
          division_department: formData.department_id
        });
        if (response.data.status === 1) {
          toast.success('Division created successfully');
          setShowDialog(false);
          fetchDivisions();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  }

  async function handleDelete(id, name) {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    try {
      const response = await api.delete(`/api/department/${id}`);
      if (response.data.status === 1) {
        toast.success('Division deleted successfully');
        fetchDivisions();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Delete failed');
    }
  }
}

export default DepartmentsPage;
